import { create } from 'zustand';

/**
 * @fileoverview Navigation loading store (Zustand)
 * @description
 * React/Zustand port of the Angular `NavigationLoadingService`. Tracks
 * navigation and guard-check state so a loading overlay can be shown during
 * route transitions. Signal names (`isLoading`, `isGuardChecking`) and the
 * derived `loading` accessor are reproduced 1:1.
 *
 * Adaptation notes:
 * - Angular subscribed to the Angular Router's event stream in its constructor
 *   and flipped state on NavigationStart/GuardsCheckStart/End/NavigationEnd/etc.
 *   Per the React conversion rules, stores never touch the router, so the
 *   state-flipping is exposed as explicit semantic methods
 *   (`navigationStarted`, `guardsCheckStart`, `guardsCheckEnd`,
 *   `navigationEnded`). The React Router integration layer calls these from its
 *   own navigation lifecycle. Direct setters are also exposed.
 */

interface NavigationLoadingState {
  isLoading: boolean;
  isGuardChecking: boolean;

  /** Derived: true if navigating or checking guards (was the `loading` getter). */
  loading: () => boolean;

  setLoading: (value: boolean) => void;
  setGuardChecking: (value: boolean) => void;

  navigationStarted: () => void;
  guardsCheckStart: () => void;
  guardsCheckEnd: () => void;
  navigationEnded: () => void;
}

export const useNavigationLoadingStore = create<NavigationLoadingState>((set, get) => ({
  isLoading: false,
  isGuardChecking: false,

  loading: () => get().isLoading || get().isGuardChecking,

  setLoading: (value) => set({ isLoading: value }),
  setGuardChecking: (value) => set({ isGuardChecking: value }),

  navigationStarted: () => set({ isLoading: true }),
  guardsCheckStart: () => set({ isGuardChecking: true }),
  guardsCheckEnd: () => set({ isGuardChecking: false }),
  navigationEnded: () => set({ isLoading: false, isGuardChecking: false }),
}));
